import React, { useEffect, useMemo } from "react";

import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Stack, Typography } from "@mui/material";
import { BinDeleteIcon, CustomizeTable } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { Menu } from "components/popups";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { defaultDocumentReportQueries } from "constants/documents";
import { useActionHook, useModals, usePrompt } from "hooks";
import { useReportsActions } from "store";
import { CubicData, DocumentReportQueries, DocumentSectionKPI, ICubic } from "store/types";

import { RoleName } from "../../../../constants";
import { useContentContext } from "../../Content/content-context";
import { EditKPIs } from "../../Report-sections-forms/EditKPIs";
import { EditQueries } from "../../Report-sections-forms/EditQueries";
import { ReportQueries } from "../ReportQueries";
import { KPI } from "./KPI";

interface KPISectionProps {
  section: DocumentSectionKPI;
}

export const KPISection: React.FC<KPISectionProps> = ({ section }) => {
  const openPrompt = usePrompt();
  const { deleteModal } = useModals();
  const { report_queries: globalQueries, cubicsQueue, deleteSection, updateSection } = useContentContext();
  const { getCubicAction } = useReportsActions();

  const {
    data = {},
    action: getCubic,
    loading: getCubicLoading
  } = useActionHook(getCubicAction, {
    defaultLoadingState: true
  });
  const { action: updateSectionAction, loading: updateSectionLoading } = useActionHook(updateSection);

  const { settings, queries } = section.report_data;

  const resultQueries: DocumentReportQueries = useMemo(() => {
    return {
      ...defaultDocumentReportQueries,
      ...(queries || globalQueries)
    };
  }, [queries, globalQueries]);

  useEffect(() => {
    cubicsQueue.addRequest({
      func: getCubic,
      args: [
        {
          cubics: settings.reduce<ICubic>((acc, { filter, title }, index) => {
            acc[`cubic${index + 1}`] = {
              filter,
              title
            };
            return acc;
          }, {} as ICubic),
          pageType: "document",
          use_accounting_date: false,
          ...resultQueries
        }
      ]
    });
  }, [globalQueries]);

  const updateReportData = async (data: DocumentSectionKPI["report_data"]) => {
    const updatedSection = (await updateSectionAction({
      id: section.id,
      report_data: data
    })) as DocumentSectionKPI;

    // refetch cubic data
    getCubic({
      cubics: updatedSection.report_data.settings.reduce<ICubic>((acc, { filter, title }, index) => {
        acc[`cubic${index + 1}`] = {
          filter,
          title
        };
        return acc;
      }, {} as ICubic),
      pageType: "document",
      use_accounting_date: false,
      ...(updatedSection.report_data.queries || resultQueries)
    });
  };

  const updateSettings = async () => {
    const updatedSettings = await openPrompt({
      Component: EditKPIs,
      props: {
        data: settings.map(({ title, filter }) => ({
          title,
          filter
        }))
      }
    });

    if (!updatedSettings) return;

    updateReportData({
      settings: updatedSettings,
      ...(queries && { queries })
    });
  };

  const updateQueries = async () => {
    const updateQueries = await openPrompt({
      Component: EditQueries,
      props: { data: resultQueries }
    });

    if (!updateQueries) return;

    updateReportData({
      queries: updateQueries,
      settings: settings
    });
  };

  return (
    <ProgressOverlay
      inProgress={getCubicLoading || updateSectionLoading}
      containerSX={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "16px",
        borderRadius: "16px",
        boxShadow: "sm",
        bgcolor: "#fff",
        width: "100%",
        "&:hover": {
          "& .kpi-section-menu": {
            opacity: 1
          }
        }
      }}
    >
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "24px", color: "#071F35" }}>KPIs</Typography>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
          <Menu
            keepTriggerEnabled
            iconButtonProps={{
              sx: { opacity: 0, transition: "opacity .15s linear" },
              size: "sm",
              className: "kpi-section-menu"
            }}
            options={[
              { title: "Customize KPIs", onClick: updateSettings, icon: <CustomizeTable /> },
              {
                title: "Parameter set",
                icon: <ViewWeekOutlinedIcon />,
                onClick: updateQueries
              },
              {
                title: "Delete",
                icon: <BinDeleteIcon />,
                onClick: () =>
                  deleteModal({
                    action: () => deleteSection({ sectionId: section.id })
                  })
              }
            ]}
          />
        </RoleComponentGuard>
      </Stack>
      {section.report_data.queries && <ReportQueries {...section.report_data.queries} />}
      <Stack
        sx={{
          flexDirection: "row",
          gap: "10px",
          alignItems: "stretch",
          flexGrow: 1,
          minHeight: 80,
          flexWrap: "wrap",
          "& > *": {
            minHeight: "80px"
          }
        }}
      >
        {(Object.entries(data) as Array<[string, CubicData]>).map(([key, value]) => (
          <KPI key={key} {...value} />
        ))}
      </Stack>
    </ProgressOverlay>
  );
};
